import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import NavigationBar from '../../components/ui/NavigationBar';
import PremiumGate from '../../components/PremiumGate';
import ObjectionCard from './components/ObjectionCard';
import SearchBar from './components/SearchBar';
import PracticeInterface from './components/PracticeInterface';
import StatsOverview from './components/StatsOverview';
import Icon from '../../components/AppIcon';
import Select from '../../components/ui/Select';
import { gamificationService } from '../../services/gamificationService';
import LoadingState from '../../components/ui/LoadingState';
import { objectionsService } from '../../services/objectionsService';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';

const ObjectionDojo = () => {
  const { user, profile } = useAuth();
  const { tenantId, tenantSlug } = useTenant();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedObjection, setSelectedObjection] = useState(null);
  const [objections, setObjections] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [objectionStatuses, setObjectionStatuses] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [stats, setStats] = useState(null);
  const ITEMS_PER_PAGE = 10;
  
  // For tenanted users, show tenant industry instead of user profile industry
  const displayIndustry = (tenantSlug && tenantSlug !== 'default') ? tenantSlug : userProfile?.industry;

  // Load user profile and objections
  useEffect(() => {
    if (user?.id) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load user profile
      const profile = await objectionsService?.getUserProfile(user?.id);
      setUserProfile(profile);

      // Load filtered objections
      const fetchedObjections = await objectionsService?.getFilteredObjections(user?.id, tenantId);
      
      // Load user attempts to calculate status for each objection
      const attempts = await objectionsService?.getUserAttempts(user?.id);
      
      // Calculate status for each objection
      const statusMap = {};
      fetchedObjections?.forEach(objection => {
        const objectionAttempts = attempts?.filter(a => a?.objection_id === objection?.id);
        statusMap[objection?.id] = calculateObjectionStatus(objectionAttempts);
      });
      
      setObjectionStatuses(statusMap);
      setObjections(fetchedObjections);

      // Load real user stats
      const userStats = await objectionsService?.getUserStats(user?.id);
      setStats(userStats);
    } catch (err) {
      console.error('Error loading objection dojo data:', err);
      setError(err?.message || 'Failed to load objections');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Calculate objection status based on user attempts
   * @param {Array} attempts - User's attempts for this objection
   * @returns {Object} - Status with label and score
   */
  const calculateObjectionStatus = (attempts) => {
    if (!attempts || attempts?.length === 0) {
      return { label: 'Not Started', color: 'text-muted-foreground bg-muted', avgScore: 0 };
    }

    // Calculate average score from last 3 attempts
    const recentAttempts = attempts?.slice(0, 3);
    const avgScore = Math.round(
      recentAttempts?.reduce((sum, att) => sum + (att?.lumo_score || 0), 0) / recentAttempts?.length
    );

    if (avgScore >= 85) {
      return { label: 'Mastered', color: 'text-success bg-success/10', avgScore };
    } else if (avgScore >= 70) {
      return { label: 'Proficient', color: 'text-primary bg-primary/10', avgScore };
    } else if (avgScore >= 50) {
      return { label: 'Needs Work', color: 'text-warning bg-warning/10', avgScore };
    } else {
      return { label: 'Struggling', color: 'text-error bg-error/10', avgScore };
    }
  };

  // Filter objections based on search and category
  const filteredObjections = useMemo(() => {
    let items = objections;

    if (searchQuery) {
      const q = searchQuery?.toLowerCase();
      items = items?.filter(objection => (
        objection?.display_label?.toLowerCase()?.includes(q) ||
        objection?.label?.toLowerCase()?.includes(q) ||
        objection?.tags?.toLowerCase()?.includes(q) ||
        objection?.default_response?.toLowerCase()?.includes(q)
      ));
    }

    if (selectedCategory && selectedCategory !== 'all') {
      items = items?.filter(objection => objection?.category_name === selectedCategory);
    }

    return items;
  }, [objections, searchQuery, selectedCategory]);

    // Calculate pagination
    const totalPages = Math.ceil(filteredObjections?.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginatedObjections = filteredObjections?.slice(startIndex, endIndex);

    // Reset to page 1 when search changes
    useEffect(() => {
      setCurrentPage(1);
    }, [searchQuery]);

    // Reset to page 1 when category changes
    useEffect(() => {
      setCurrentPage(1);
    }, [selectedCategory]);

  // Build category options
  const categoryOptions = useMemo(() => {
    const unique = Array.from(new Set(objections?.map(o => o?.category_name || 'Uncategorized')));
    return [{ label: 'All Categories', value: 'all' }, ...unique?.map(name => ({ label: name, value: name }))];
  }, [objections]);

  const handleObjectionSelect = (objection) => {
    // Find the complete objection data from our fetched list
    const completeObjection = objections?.find(obj => obj?.id === objection?.id);
    setSelectedObjection(completeObjection);
  };

  const handlePracticeClose = () => {
    setSelectedObjection(null);
  };

  const handlePracticeSubmit = async (response, feedback) => {
    // Update achievements: streak and missions
    try {
      await gamificationService.updateStreak('objection');
      
      const missions = await gamificationService.getTodaysMissions();
      const objectionType = selectedObjection?.type || 'general';
      
      for (const mission of missions) {
        if ((mission.mission_type === 'objection' || mission.mission_type === 'combo') && !mission.isCompleted) {
          const currentProgress = mission.progress || {};
          const handledTypes = new Set(currentProgress.objection_types_handled || []);
          handledTypes.add(objectionType);
          
          const scores = currentProgress.objection_scores || [];
          const score = feedback?.score || 75; // Extract score from feedback if available
          scores.push(score);
          const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
          
          await gamificationService.updateMissionProgress(mission.progressId, {
            objections_handled: (currentProgress.objections_handled || 0) + 1,
            unique_objection_types: handledTypes.size,
            objection_types_handled: Array.from(handledTypes),
            objection_scores: scores,
            min_avg_score: Math.round(avgScore)
          });
        }
      }

      // Refresh stats after submission
      const updatedStats = await objectionsService?.getUserStats(user?.id);
      setStats(updatedStats);
    } catch (gamError) {
      console.error('⚠️ Achievements update failed:', gamError);
    }
  };

  // Manual skill tier changes are disabled; progression is system-controlled.

  const formatSkillTier = (tier) => {
    return tier?.charAt(0)?.toUpperCase() + tier?.slice(1);
  };

  const formatIndustry = (industry) => {
    if (!industry) return 'Not Set';
    return industry?.split('_')?.map(word => 
      word?.charAt(0)?.toUpperCase() + word?.slice(1)
    )?.join(' ');
  };

  if (loading) {
    return (
      <>
        <NavigationBar userStreak={7} /><div className="main-content bg-background">
          <LoadingState message="Loading objections..." />
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Objection Dojo - EME</title>
        <meta name="description" content="Master sales objections with AI-powered coaching and practice sessions tailored to your industry." />
      </Helmet>
      <NavigationBar userStreak={7} />
      <PremiumGate feature="objectionDojo" requiredPlan="Pro">
        <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/10 rounded-xl border border-primary/20">
                  <Icon name="Shield" className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-foreground">Objection Dojo</h1>
                  <p className="text-muted-foreground">Master objection handling with AI-powered coaching and practice</p>
                </div>
              </div>
            </div>

            {/* User Profile Pills */}
            {userProfile && (
              <div className="mb-8 flex items-center gap-3 flex-wrap">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-full">
                  <Icon name="Briefcase" size={16} className="text-primary" />
                  <span className="text-sm font-medium text-foreground">
                    {formatIndustry(displayIndustry)}
                  </span>
                </div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-full">
                  <Icon name="TrendingUp" size={16} className="text-primary" />
                  <span className="text-sm font-medium text-foreground">
                    {formatSkillTier(userProfile?.skill_level)}
                  </span>
                </div>
              </div>
            )}

            {/* Mastery Stats */}
            {stats && (
              <div className="mb-8">
                <StatsOverview stats={stats} />
              </div>
            )}

            {/* Objections List */}
            <div className="mb-8">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-foreground">
                  Practice Objections
                </h2>
                <p className="text-muted-foreground mt-1">Master handling common sales objections with AI-powered feedback</p>
              </div>

              {/* Controls */}
              <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex-1 max-w-xl">
                  <SearchBar searchQuery={searchQuery} onSearchChange={setSearchQuery} />
                </div>
                <div className="w-full sm:w-56">
                  <Select
                    options={categoryOptions}
                    value={selectedCategory}
                    onChange={setSelectedCategory}
                    placeholder="Category"
                    clearable
                  />
                </div>
              </div>

              {filteredObjections?.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 mb-8">
                    {paginatedObjections?.map(objection => {
                      const status = objectionStatuses?.[objection?.id] || { label: 'Not Started', color: 'text-muted-foreground bg-muted', avgScore: 0 };
                      return (
                        <ObjectionCard 
                          key={objection?.id}
                          objection={{
                            id: objection?.id,
                            statement: objection?.display_label,
                            status: status?.label,
                            statusColor: status?.color,
                            avgScore: status?.avgScore,
                            difficulty: objection?.difficulty,
                            tags: objection?.tags,
                            categoryName: objection?.category_name
                          }}
                          onSelect={handleObjectionSelect}
                        />
                      );
                    })}
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-4 pt-8 border-t border-border">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="px-6 py-2 bg-card border-2 border-border rounded-lg font-semibold text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        Previous
                      </button>
                      <span className="text-muted-foreground font-semibold">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="px-6 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-card rounded-xl p-12 border-2 border-border text-center">
                  <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">No objections found</h3>
                  <p className="text-muted-foreground">
                    {searchQuery ? 'Try adjusting your search query' : 'No objections available for your industry and skill level'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </PremiumGate>

      {/* Practice Interface Modal */}
      {selectedObjection && (
        <PracticeInterface
          objection={selectedObjection}
          onClose={handlePracticeClose}
          onSubmit={handlePracticeSubmit}
        />
      )}
    </>
  );
};

export default ObjectionDojo;